import type { DragEndEvent, DragStartEvent } from '@dnd-kit/react';
import { DragDropProvider, useDragDropMonitor, useDragOperation } from '@dnd-kit/react';
import { useReducedMotion } from '@words-happen/ui-components';
import type React from 'react';
import { useCallback, useMemo, useRef } from 'react';
import type { TileData } from '../board/board-types';
import { tilePointerSensors } from '../board/drag-sensors';
import { TileDragOverlay } from '../board/tile-drag-overlay';
import { rackSpan, tilesWithin } from './rack-capacity';
import { moveTile } from './rack-reorder';
import styles from './tile-rack.module.scss';
import { TileRackSlot } from './tile-rack-slot';
import { useRackReorderPreview } from './use-rack-reorder-preview';
import { useTileSlide } from './use-tile-slide';

export interface TileRackProps {
  /**
   * Id namespace for this rack's slots (`${id}-slot-0` …). Give racks sharing one provider (two
   * players' racks, say) distinct ids so their slot ids don't collide.
   */
  id?: string;
  /**
   * Tiles in rack order. Tiles beyond what `capacity` characters hold are not rendered — the rack
   * shows the leading run of them that fits.
   */
  tiles: TileData[];
  /**
   * How many characters the rack holds, not how many tiles: a two-character tile takes two of these
   * places, the same two cells it takes on the board. Whatever is left over renders as empty places.
   */
  capacity?: number;
  cellSize?: number;
  gap?: number;
  /** Draws each tile's points badge and says its points in the tile's name. */
  showPoints?: boolean;
  draggable?: boolean;
  /**
   * `true` (default): TileRack provisions its own `DragDropProvider`, sensors and drag overlay — fully
   * self-contained. `false`: skips that provisioning so TileRack can be composed under a
   * `DragDropProvider` a parent screen owns, alongside `Board` (also `standalone={false}`) — the
   * composition that lets a tile be dragged out of the rack and dropped directly onto the board. That
   * parent renders the one `TileDragOverlay` the provider may have; see `Board`.
   */
  standalone?: boolean;
  /**
   * Fires when a rack tile is released over this same rack, in its new order — the order the rack has
   * been showing while the tile was in the air, tiles shuffled aside to leave a gap for it.
   */
  onTileReorder?: (tiles: TileData[]) => void;
  onTilePickup?: (tile: TileData) => void;
  /**
   * Fires when a rack tile's drag ends with the pointer off this rack — over the board, over another
   * rack, or over nothing at all. Inspect `event.operation.target` to tell those apart: Board's
   * `dropId` in a shared DragDropProvider, another rack's slot id, or `null` for a drop over nothing.
   * See `cellFromDragEvent` for turning a drop over Board into a target cell. TileRack does not remove
   * the tile itself — update `tiles` from here.
   */
  onTileDragEnd?: (tile: TileData, event: DragEndEvent) => void;
  /**
   * Fires when a tile is tapped or clicked without being dragged, or when `Enter` / `Space` is pressed
   * on a focused tile. Only while `draggable`.
   */
  onTileActivate?: (tile: TileData) => void;
  /** Said after each tile's name when `onTileActivate` is set, naming what activating it does. */
  activateLabel?: string;
  ariaLabel?: string;
}

const slotId = (rackId: string, index: number): string => `${rackId}-slot-${index}`;

/** How long after a drag ends a click on a tile is taken to be that drag's own, and ignored. */
const CLICK_AFTER_DRAG_MS = 250;

type TileRackInnerProps = Omit<TileRackProps, 'standalone'>;

/** `TileRack`'s full implementation — see `Board`'s `BoardInner` for why handlers use `useDragDropMonitor`. */
const TileRackInner: React.FC<TileRackInnerProps> = ({
  id = 'rack',
  tiles,
  capacity = 8,
  cellSize = 40,
  gap = 4,
  showPoints = true,
  draggable = true,
  onTileReorder,
  onTilePickup,
  onTileDragEnd,
  onTileActivate,
  activateLabel,
  ariaLabel = 'Tile rack',
}) => {
  const rackRef = useRef<HTMLDivElement>(null);
  const dragEndedAtRef = useRef(0);
  const reducedMotion = useReducedMotion();
  const visibleTiles = useMemo(() => tilesWithin(tiles, capacity), [tiles, capacity]);
  /** Places the tiles leave open — one empty slot each, since a place is one character wide. */
  const empties = capacity - rackSpan(visibleTiles);

  // The order to render — mid-drag, the tiles already shuffled aside to leave a gap where the tile in
  // hand would land — and the answer to where that is, which drag end below commits.
  const { order, landingIndexAt } = useRackReorderPreview({ rackRef, tiles: visibleTiles });
  const slide = useTileSlide(!reducedMotion);

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const tile = tiles.find((t) => t.id === event.operation.source?.id);
      if (tile) onTilePickup?.(tile);
    },
    [tiles, onTilePickup],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      dragEndedAtRef.current = Date.now();
      // A cancelled drag always returns the tile to its own origin slot, never routed through
      // `onTileDragEnd`.
      if (event.canceled) return;

      const tile = tiles.find((t) => t.id === event.operation.source?.id);
      if (!tile) return;

      // Where the tile goes is the gap the player has been looking at all through the drag, and
      // whether it goes there at all is the same question the preview has been answering: a release
      // over the rack drops into the gap, and one anywhere else is the owner's to deal with. By drag
      // end the last move has been committed, so the operation's position is the pointer's final
      // resting place.
      const pointer = event.operation.position.current;
      const toIndex = landingIndexAt(pointer.x, pointer.y);
      if (toIndex != null) {
        const fromIndex = tiles.findIndex((t) => t.id === tile.id);
        if (fromIndex !== -1 && toIndex !== fromIndex) {
          onTileReorder?.(moveTile(tiles, fromIndex, toIndex));
        }
        return;
      }

      onTileDragEnd?.(tile, event);
    },
    [tiles, landingIndexAt, onTileReorder, onTileDragEnd],
  );

  // The listener object is memoized so useDragDropMonitor's subscription effect only re-runs when a
  // handler itself actually changes — not on every unrelated re-render (e.g. a sibling's drag updating
  // this component's props) — which would otherwise churn unsubscribe/resubscribe through the gesture.
  const dndMonitorListeners = useMemo(
    () => ({
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
    }),
    [handleDragStart, handleDragEnd],
  );
  useDragDropMonitor(dndMonitorListeners);

  // Read from dnd-kit's own drag operation rather than tracking "which tile is being dragged" as
  // separate local state set imperatively in the handlers above: that copy can only ever be as reliable
  // as every onDragStart/onDragEnd firing in order, and a very fast pickup-and-release is exactly the
  // kind of gesture where handler delivery can't be assumed. `source` is dnd-kit's own source of truth
  // for "what's being dragged right now" and can never drift out of sync with it — a tile can never end
  // up stuck ghosted because a cancel or end event went astray.
  const { source } = useDragOperation();
  const draggedTileId = tiles.find((t) => t.id === source?.id)?.id ?? null;

  const handleTileKeyDown = useCallback(
    (e: React.KeyboardEvent, tile: TileData) => {
      if (!draggable) return;
      const index = tiles.findIndex((t) => t.id === tile.id);
      if (index === -1) return;

      if ((e.key === 'Enter' || e.key === ' ') && onTileActivate) {
        e.preventDefault();
        onTileActivate(tile);
      } else if (e.key === 'ArrowLeft' && index > 0) {
        e.preventDefault();
        onTileReorder?.(moveTile(tiles, index, index - 1));
      } else if (e.key === 'ArrowRight' && index < visibleTiles.length - 1) {
        e.preventDefault();
        onTileReorder?.(moveTile(tiles, index, index + 1));
      }
    },
    [draggable, tiles, visibleTiles.length, onTileReorder, onTileActivate],
  );

  const handleTileClick = useMemo(
    () =>
      onTileActivate &&
      ((tile: TileData) => {
        if (Date.now() - dragEndedAtRef.current < CLICK_AFTER_DRAG_MS) return;
        onTileActivate(tile);
      }),
    [onTileActivate],
  );

  return (
    <div ref={rackRef} role="group" aria-label={ariaLabel} className={styles.rack}>
      {/*
       * The occupied slots come first and the empty places after them, never interleaved: the
       * reorder preview measures this rack's first `tiles.length` children as the tiles' own slots
       * (see `useRackReorderPreview`), so a ghost among them would be measured as a tile.
       *
       * Keyed by the tile's own id, not the slot index, so a moved tile keeps its DOM node — and
       * with it, focus — as it slides to a new position; an empty place keys off its index, having
       * no tile identity of its own.
       */}
      {order.map((tile, index) => (
        <TileRackSlot
          key={tile.id}
          slotId={slotId(id, index)}
          region={id}
          tile={tile}
          slideRef={slide.ref(tile.id)}
          cellSize={cellSize}
          gap={gap}
          showPoints={showPoints}
          draggable={draggable}
          isDragging={tile.id === draggedTileId}
          onKeyDown={handleTileKeyDown}
          onClick={handleTileClick}
          activateLabel={onTileActivate ? activateLabel : undefined}
        />
      ))}
      {Array.from({ length: empties }, (_, index) => {
        const slot = slotId(id, order.length + index);
        return (
          <TileRackSlot
            key={slot}
            slotId={slot}
            region={id}
            tile={null}
            cellSize={cellSize}
            gap={gap}
            showPoints={showPoints}
            draggable={draggable}
            isDragging={false}
            onKeyDown={handleTileKeyDown}
          />
        );
      })}
    </div>
  );
};

/**
 * A rack of `capacity` characters (default 8) — a tile takes one place per character of its face, so
 * eight places hold eight single letters, or four two-character tiles, or any mixture coming to
 * eight. Tiles are rendered with the same `BoardTileFace` chrome as the board, so a tile's look never
 * changes as it moves between rack and board. Places no tile fills render as a dashed ghost
 * placeholder one character wide, matching Board's origin-ghost styling.
 *
 * Tiles reorder by dragging one along the rack — the others slide aside as it passes each of them, so
 * the gap the tile will drop into is open and visible the whole way — or, with a tile focused, by
 * `ArrowLeft` / `ArrowRight`. For dragging a tile out onto the board, see `standalone` and
 * `onTileDragEnd`.
 */
export const TileRack: React.FC<TileRackProps> = ({ standalone = true, ...props }) => {
  if (!standalone) return <TileRackInner {...props} />;

  return (
    <DragDropProvider sensors={tilePointerSensors}>
      <TileRackInner {...props} />
      <TileDragOverlay />
    </DragDropProvider>
  );
};
